import z from "zod";

import { DEFAULT_DOCUMENT_FILTERS, DocumentFilters } from "components/documents/utils";
import { DEFAULT_FILTERS as DEFAULT_FILTERS_NOTICE, NoticeFilters } from "components/notices/utils";
import { TooltipComponentProps } from "components/ui/tooltip/Tooltip";
import { SavedViewResults, useSavedViewsDataType } from "lib/hooks/api/saved_views/useSavedViews";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { useURLState } from "lib/hooks/useURLState";
import { routes } from "lib/routes";
import { createSavedViewSelectedEvent } from "lib/savedViewTracking";
import { targetToUrl } from "lib/spaNavigation";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { SavedViewDataType } from "lib/types/graphQLEnums";
import { SavedViewDataTypeUnion, SavedViewGeneric } from "lib/types/savedViews";
import { isDefined } from "lib/utils";

// The minimal saved-view shape the saved_searches modals (Edit/EditAlerts/Duplicate/Delete) need.
export type ModalView = {
  id: string;
  name: string;
  description?: string | null;
  isStandardView?: boolean;
};

const SAVED_VIEW_URL_MAP: Record<SavedViewDataTypeUnion, string> = {
  notice: routes.notices,
  document: routes.documents,
  noticeSemantic: routes.noticesV2,
};
const URL_TO_SAVED_VIEW_DATA_TYPE_MAP: Record<string, SavedViewDataTypeUnion> = Object.entries(
  SAVED_VIEW_URL_MAP,
).reduce(
  (acc, [dataType, url]) => {
    acc[url] = dataType as SavedViewDataTypeUnion;
    return acc;
  },
  {} as Record<string, SavedViewDataTypeUnion>,
);

export const generateSavedViewTarget = (
  id: string,
  dataType: SavedViewDataTypeUnion,
): { to: string; search: Record<string, unknown> } => {
  return { to: SAVED_VIEW_URL_MAP[dataType], search: { current_view_id: id } };
};

export const generateSavedViewUrl = (id: string, dataType: SavedViewDataTypeUnion) =>
  targetToUrl(generateSavedViewTarget(id, dataType));

/**
 * Limited functionality compared to useSavedViews in notices/documents/etc
 * but functionality works across all data types
 */
export function useSavedViewsGeneric() {
  const { logEvent } = useTracking();

  const [, setUrlViewId] = useURLState("current_view_id", undefined, z.string().optional());

  const [, setShowNoViewReplaceState] = useURLState(
    "showNoView",
    false,
    z.union([z.boolean(), z.stringbool()]).optional(),
    { replaceState: true },
  );

  const [, setUrlFiltersNoticeReplaceState] = useURLState<NoticeFilters | undefined>(
    "noticeFilters",
    DEFAULT_FILTERS_NOTICE,
    // We don't need to parse the filters here so we just return the default filters so there is always a difference when setting to undefined
    () => DEFAULT_FILTERS_NOTICE,
    { encoder: "compress", replaceState: true },
  );

  const [, setUrlFiltersDocumentReplaceState] = useURLState<DocumentFilters | undefined>(
    "documentFilters",
    DEFAULT_DOCUMENT_FILTERS,
    // We don't need to parse the filters here so we just return the default filters so there is always a difference when setting to undefined
    () => DEFAULT_DOCUMENT_FILTERS,
    { replaceState: true },
  );

  /**
   * If the current page is the same data type as the selected view, change it. If not, do nothing.
   * This means this should be used with a <a> link with a href and this should be the onClick handler.
   */
  const setSelectedView = (
    viewId: string,
    dataType: SavedViewDataTypeUnion,
    event?: React.MouseEvent,
    viewName?: string,
    context?: string,
  ) => {
    logEvent(
      EventNames.viewSelected,
      createSavedViewSelectedEvent(dataType, viewId, viewName, context),
    );

    const currentUrl = new URL(window.location.href);
    const currentDataType = URL_TO_SAVED_VIEW_DATA_TYPE_MAP[currentUrl.pathname];

    if (currentDataType !== dataType) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    setUrlViewId(viewId);
    setShowNoViewReplaceState(undefined);

    // Remove filter parameters from URL when selecting a view
    switch (dataType) {
      case SavedViewDataType.Notice:
        setUrlFiltersNoticeReplaceState(undefined);
        break;
      case SavedViewDataType.Document:
        setUrlFiltersDocumentReplaceState(undefined);
        break;
      case SavedViewDataType.NoticeSemantic:
        // TODO: When we added URL state for filters
        break;
    }
  };

  return {
    setSelectedView,
  };
}

export const viewActionTooltip = (
  action: "edit" | "delete",
  view: Pick<SavedViewGeneric, "isStandardView" | "owner"> | undefined,
  userGuid: string,
): Pick<TooltipComponentProps, "content" | "side"> | undefined => {
  const suffix = action === "edit" ? `Duplicate search to edit` : "Cannot be deleted";

  if (view?.isStandardView) {
    return {
      content: `Template search. ${suffix}`,
      side: "left",
    };
  }
  if (view?.owner?.guid !== userGuid) {
    return {
      content: `Owned by another user. ${suffix}`,
      side: "left",
    };
  }
};

export const viewDataTypeUnionToEnum = (dataType: `${SavedViewDataType}`): SavedViewDataType => {
  return dataType as SavedViewDataType;
};

const RECENT_VIEW_LOCAL_STORAGE_KEY: Record<SavedViewDataTypeUnion, string> = {
  notice: "RECENT_VIEWS",
  document: "RECENT_DOCUMENT_VIEWS",
  noticeSemantic: "RECENT_NOTICE_SEMANTIC_VIEWS",
};

export const useRecentViews = <DataType extends SavedViewDataType>(
  dataType: DataType,
  hydrate: boolean = true,
): {
  recentViewsIds: string[];
  setRecentViewsIds: (recentViewsIds: string[]) => void;
  recentViews: SavedViewResults<DataType>;
} => {
  const [recentViewsIds, setRecentViewsIds] = useLocalStorage<string[]>(
    RECENT_VIEW_LOCAL_STORAGE_KEY[dataType],
    [],
  );

  const { data: savedViews } = useSavedViewsDataType(
    DATA_TYPE_TO_REQUEST_VERSION[dataType],
    {},
    { page: 1, pageSize: 1000 },
    { enabled: hydrate },
  );

  const hydratedRecentViews = recentViewsIds
    .map((id: string) => savedViews?.results?.find((v) => v.id === id))
    .filter(isDefined) as SavedViewResults<DataType>;

  return {
    recentViewsIds,
    setRecentViewsIds,
    recentViews: hydratedRecentViews,
  };
};

export const DATA_TYPE_TO_EVENT_VERSION: Record<SavedViewDataTypeUnion, EventDataTypes> = {
  notice: EventDataTypes.notices,
  document: EventDataTypes.documents,
  noticeSemantic: EventDataTypes.notices, // Dan wants this to match notices
};

export const DATA_TYPE_TO_CREATE_VERSION: Record<SavedViewDataTypeUnion, SavedViewDataType> = {
  notice: SavedViewDataType.Notice,
  document: SavedViewDataType.Document,
  noticeSemantic: SavedViewDataType.NoticeSemantic,
};

export const DATA_TYPE_TO_REQUEST_VERSION: Record<SavedViewDataTypeUnion, SavedViewDataType> = {
  notice: SavedViewDataType.Notice,
  document: SavedViewDataType.Document,
  noticeSemantic: SavedViewDataType.NoticeSemantic,
};
